import { Box, Chip, Paper, Skeleton, TablePagination, Typography } from "@mui/material";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { DateTime } from "luxon";
import { useRef, useState } from "react";
import { BaseModalComponent } from "../common/BaseModalComponent";
import { colors } from "../ui/colors";
import { EventDeliveryLogDetails } from "./EventDeliveryLogDetails";
import { useGetEventDeliveryLogs } from "./hooks/useGetEventDeliveryLogs";
import { IEventDeliveryLog, TEventDeliveryLogSource } from "./interfaces";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

const formatTimestamp = (value: string) => {
  const parsed = DateTime.fromISO(value);
  if (!parsed.isValid) return value;
  return parsed.toLocal().toFormat("MMM d, yyyy • HH:mm");
};

const getHttpStatusText = (code?: number | null): string => {
  if (!code) return "";
  switch (code) {
    case 200:
      return "OK";
    case 201:
      return "Created";
    case 202:
      return "Accepted";
    case 204:
      return "No Content";
    case 400:
      return "Bad Request";
    case 401:
      return "Unauthorized";
    case 403:
      return "Forbidden";
    case 404:
      return "Not Found";
    case 408:
      return "Timeout";
    case 429:
      return "Too Many Requests";
    case 500:
      return "Internal Error";
    case 502:
      return "Bad Gateway";
    case 503:
      return "Service Unavailable";
    case 504:
      return "Gateway Timeout";
    default:
      if (code >= 200 && code < 300) return "OK";
      if (code >= 400 && code < 500) return "Error";
      if (code >= 500) return "Server Error";
      return "";
  }
};

const getStatusBadge = (log: IEventDeliveryLog) => {
  const isPoll = log.source === "POLL";
  const status = log.http_status;
  const hasError = Boolean(log.error);

  const isOk = (status != null && status >= 200 && status < 300) || (isPoll && !hasError);
  const isError = (status != null && status >= 400) || (hasError && !isPoll);

  let statusText = "";
  if (status != null) {
    const text = getHttpStatusText(status);
    statusText = text ? `${status} ${text}` : `HTTP ${status}`;
  } else if (isPoll) {
    statusText = hasError ? "Poll Failed" : "200 OK";
  } else {
    statusText = hasError ? "Delivery Failed" : "Pending";
  }

  if (isOk) {
    return {
      label: statusText,
      bg: "rgba(34, 197, 94, 0.12)",
      color: "#15803d",
      border: "1px solid rgba(34, 197, 94, 0.30)",
      dotColor: "#22c55e",
      leftBorder: "#22c55e",
      isOk: true,
      isError: false,
    };
  }

  if (isError) {
    return {
      label: statusText,
      bg: "rgba(239, 68, 68, 0.12)",
      color: "#b91c1c",
      border: "1px solid rgba(239, 68, 68, 0.30)",
      dotColor: "#ef4444",
      leftBorder: "#ef4444",
      isOk: false,
      isError: true,
    };
  }

  return {
    label: statusText,
    bg: "rgba(245, 158, 11, 0.12)",
    color: "#b45309",
    border: "1px solid rgba(245, 158, 11, 0.30)",
    dotColor: "#f59e0b",
    leftBorder: "#f59e0b",
    isOk: false,
    isError: false,
  };
};

const getSourceBadgeStyle = (source: TEventDeliveryLogSource) => {
  if (source === "WEBHOOK") {
    return {
      label: "WEBHOOK",
      bg: "rgba(124, 58, 237, 0.10)",
      color: "#7c3aed",
      border: "1px solid rgba(124, 58, 237, 0.22)",
    };
  }
  return {
    label: "POLL",
    bg: "rgba(14, 165, 233, 0.10)",
    color: "#0284c7",
    border: "1px solid rgba(14, 165, 233, 0.22)",
  };
};

const getFilterChipStyle = (optionId: TEventDeliveryLogSource | undefined, isSelected: boolean) => {
  if (!isSelected) {
    return {
      bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
      color: colors.gray[500],
      border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
    };
  }
  if (optionId === "WEBHOOK") {
    return {
      bgcolor: "rgba(124, 58, 237, 0.12)",
      color: "#7c3aed",
      border: "1px solid rgba(124, 58, 237, 0.30)",
    };
  }
  if (optionId === "POLL") {
    return {
      bgcolor: "rgba(14, 165, 233, 0.12)",
      color: "#0284c7",
      border: "1px solid rgba(14, 165, 233, 0.30)",
    };
  }
  return {
    bgcolor: "rgba(251, 142, 0, 0.12)",
    color: colors.orange.base,
    border: `1px solid ${colors.orange[200]}`,
  };
};

const logTitle = (log: IEventDeliveryLog) => {
  if (log.source === "POLL") return "Client poll";
  return log.event_type?.replace(/_/g, " ") || "Webhook delivery";
};

const logSubtitle = (log: IEventDeliveryLog) => {
  if (log.source === "POLL") {
    const total = typeof log.meta?.totalEvents === "number" ? log.meta.totalEvents : log.item_count;
    return `${log.item_count ?? 0} returned${typeof total === "number" ? ` • ${total} total` : ""}`;
  }
  if (log.error) return log.error;
  if (log.entity_id) return `Entity: ${log.entity_id}`;
  return "Webhook event notification";
};

export const EventDeliveryLogsList = ({ enabled = true }: { enabled?: boolean }) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [source, setSource] = useState<TEventDeliveryLogSource | undefined>(undefined);
  const [detailsLogUid, setDetailsLogUid] = useState<string | null>(null);
  const detailsTriggerRef = useRef<HTMLButtonElement | null>(null);

  const { data, isLoading, isFetching } = useGetEventDeliveryLogs({ page: page + 1, pageSize, source }, enabled);

  const logs = data?.items ?? [];
  const total = data?.totalItems ?? 0;

  const handleOpenDetails = (logUid: string) => {
    setDetailsLogUid(logUid);
    requestAnimationFrame(() => detailsTriggerRef.current?.click());
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "20px",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.02)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HistoryOutlinedIcon sx={{ color: colors.orange.base, fontSize: 20 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)" }}>Recent activity</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {[
            { id: undefined, label: "All" },
            { id: "WEBHOOK" as const, label: "Webhooks" },
            { id: "POLL" as const, label: "Polls" },
          ].map((option) => {
            const selected = source === option.id;
            const style = getFilterChipStyle(option.id, selected);
            return (
              <Chip
                key={option.label}
                size="small"
                label={option.label}
                onClick={() => {
                  setSource(option.id);
                  setPage(0);
                }}
                sx={{
                  height: 26,
                  fontSize: 12,
                  fontWeight: 700,
                  borderRadius: "8px",
                  bgcolor: style.bgcolor,
                  color: style.color,
                  border: style.border,
                }}
              />
            );
          })}
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={64} sx={{ borderRadius: "12px" }} />
          ))}
        </Box>
      ) : logs.length === 0 ? (
        <Typography sx={{ color: colors.gray[400], fontSize: 13, fontWeight: 600, py: 2 }}>No webhook or poll activity yet.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, opacity: isFetching ? 0.7 : 1, maxHeight: 480, overflowY: "auto", pr: 0.5 }}>
          {logs.map((log) => {
            const badge = getStatusBadge(log);
            const sourceStyle = getSourceBadgeStyle(log.source);
            return (
              <Box
                key={log.uid}
                onClick={() => handleOpenDetails(log.uid)}
                sx={{
                  borderRadius: "12px",
                  border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
                  borderLeft: `4px solid ${badge.leftBorder}`,
                  bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                  px: 1.75,
                  py: 1.25,
                  cursor: "pointer",
                  transition: "all 0.15s ease-in-out",
                  "&:hover": {
                    bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 14, color: colors.gray.base, lineHeight: 1.2 }}>{logTitle(log)}</Typography>
                    <Chip
                      size="small"
                      label={sourceStyle.label}
                      sx={{
                        height: 20,
                        fontSize: 10,
                        fontWeight: 800,
                        borderRadius: "6px",
                        bgcolor: sourceStyle.bg,
                        color: sourceStyle.color,
                        border: sourceStyle.border,
                        letterSpacing: "0.3px",
                      }}
                    />
                  </Box>
                  <Chip
                    size="small"
                    icon={<Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: badge.dotColor, ml: 1 }} />}
                    label={badge.label}
                    sx={{
                      height: 24,
                      fontSize: 11,
                      fontWeight: 800,
                      borderRadius: "999px",
                      bgcolor: badge.bg,
                      color: badge.color,
                      border: badge.border,
                      "& .MuiChip-icon": {
                        marginRight: -0.5,
                      },
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                  <Typography
                    sx={{
                      color: badge.isError ? "#b91c1c" : colors.gray[500],
                      fontSize: 12,
                      fontWeight: badge.isError ? 700 : 600,
                    }}
                    noWrap
                  >
                    {logSubtitle(log)}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {log.duration_ms != null && (
                      <Typography sx={{ color: colors.gray[400], fontSize: 11, fontWeight: 600 }}>{log.duration_ms}ms</Typography>
                    )}
                    <Typography sx={{ color: colors.gray[400], fontSize: 11, fontWeight: 600 }}>{formatTimestamp(log.timestamp)}</Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          labelRowsPerPage="Rows per page:"
          sx={{
            mt: 1,
            "& .MuiTablePagination-toolbar": { px: 0 },
            "& .MuiTablePagination-displayedRows": { fontSize: 12, fontWeight: 600 },
            "& .MuiTablePagination-selectLabel": { fontSize: 12, fontWeight: 600 },
            "& .MuiTablePagination-select": { fontSize: 12, fontWeight: 600 },
          }}
        />
      )}

      <BaseModalComponent
        modalKey="EventDelivery-Log-Details-Modal"
        modalLabel="Log detail"
        defaultSizes={{ width: { xs: "95%", sm: 700, md: 800 }, height: { xs: "90vh", sm: "auto" } }}
        fitContentHeight
        allowInsidePreventModal
        preventParentTrigger
        elementTrigger={<button ref={detailsTriggerRef} type="button" style={{ position: "absolute", visibility: "hidden" }} />}
        onClose={() => setDetailsLogUid(null)}
      >
        {detailsLogUid && <EventDeliveryLogDetails logUid={detailsLogUid} />}
      </BaseModalComponent>
    </Paper>
  );
};

